import { supabase } from "../config/supabase";
import { FeedbackFormDef } from "../components/canvas/feedback/forms";

export type FeedbackValues = Record<string, string | number | null>;

/** Postgres unique_violation. */
const UNIQUE_VIOLATION = "23505";

/**
 * A v4 UUID for the row's primary key.
 *
 * Not `crypto.randomUUID()` directly: that is only defined in a secure context,
 * so it is missing over plain HTTP and absent in jsdom — and unlike the other
 * callers in this app, which mint ids inside cloud-save handlers, this one runs
 * while the footer renders. An exception there takes the whole editor down.
 * The id only has to be unique enough to tell one user's retries apart.
 */
export const newSubmissionId = (): string => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.getRandomValues === "function"
  ) {
    crypto.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index++) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  // Stamp the version (4) and variant bits so the value is a well-formed UUID;
  // the column's type rejects anything else.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;

  const hex = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join("-");
};

/**
 * Thrown when the server already holds a response from this user for this form.
 *
 * A distinct type rather than a message match: the caller has to tell "your
 * answers were rejected because a row already exists" apart from a transient
 * failure, because only the former means the button should stop asking.
 */
export class FeedbackDuplicateError extends Error {
  constructor(
    message = "You've already sent us a response for this form, so this one wasn't saved.",
  ) {
    super(message);
    this.name = "FeedbackDuplicateError";
  }
}

/**
 * Did this violation come from the primary key rather than from UNIQUE
 * (user_id)?
 *
 * A PK collision can only mean one thing: an earlier attempt carrying this same
 * submission id already committed. The row the caller wanted exists and holds
 * exactly these answers, so that is a success, not a duplicate. Distinguishing
 * the two needs the constraint name — both surface as 23505.
 */
const isPrimaryKeyViolation = (error: {
  message?: string | null;
  details?: string | null;
}) => /_pkey/.test(`${error.message ?? ""} ${error.details ?? ""}`);

/**
 * Trim strings, and turn the empties into NULL.
 *
 * Without this an untouched optional field is stored as `''`, which is not the
 * same as unanswered: `WHERE expectations IS NOT NULL` would then count every
 * row and anyone reading the table over-reports how many people answered.
 */
const normalize = (values: FeedbackValues): FeedbackValues =>
  Object.fromEntries(
    Object.entries(values).map(([key, value]) => {
      if (typeof value !== "string") {
        return [key, value];
      }
      const trimmed = value.trim();
      return [key, trimmed === "" ? null : trimmed];
    }),
  );

/**
 * Insert one response.
 *
 * `user_id` is the caller's id, or null for an anonymous visitor. The RLS
 * policies already enforce that `anon` may only write NULL and `authenticated`
 * may only write its own id, so nothing here needs to branch on auth.
 *
 * `submissionId` is the row's primary key, supplied by the caller and stable
 * across retries of the same attempt. That is what makes this idempotent: if a
 * response is lost in flight the row may already be committed, and a retry
 * without it would write a second one — anonymous rows are exempt from UNIQUE
 * (user_id), so nothing downstream would catch it.
 *
 * Throws on failure — FeedbackDuplicateError when the server already holds a
 * response, a plain Error otherwise. Nothing here reports a write that did not
 * happen.
 */
export const submitFeedbackResponse = async (
  form: FeedbackFormDef,
  values: FeedbackValues,
  userId: string | null,
  submissionId: string,
): Promise<void> => {
  // No `.select()`: PostgREST would then try to return the inserted row, which
  // needs SELECT — and `anon` deliberately has none, so every anonymous
  // submission would fail even though the row was written. Omitting it sends
  // `Prefer: return=minimal`.
  const { error } = await supabase
    .from(form.table)
    .insert({ id: submissionId, user_id: userId, ...normalize(values) });

  if (!error) {
    return;
  }

  if (error.code === UNIQUE_VIOLATION) {
    // Our own earlier attempt landed; the response just never made it back.
    if (isPrimaryKeyViolation(error)) {
      return;
    }

    // A row for this user already exists — another tab, or a reopened form.
    // This used to return as if it had succeeded, on the reasoning that the row
    // the caller wanted is there. But the row that is there is the *old* one:
    // these answers were rejected and discarded, and a user who reopened the
    // form specifically to add something watched it vanish behind a thank-you
    // panel. Say so instead. Only reachable when signed in — anonymous rows are
    // NULL and never collide.
    console.error("Feedback submission rejected as duplicate:", error);
    throw new FeedbackDuplicateError();
  }

  console.error("Feedback submission failed:", error);
  throw new Error("Something went wrong. Please try again.");
};

/**
 * Has this signed-in user already responded to `form`?
 *
 * Returns null when the lookup itself failed, which the caller treats as "stay
 * hidden and retry on the next load" rather than guessing.
 */
export const hasSubmittedFeedback = async (
  form: FeedbackFormDef,
  userId: string,
): Promise<boolean | null> => {
  // `maybeSingle()` without `.limit(1)` is safe: UNIQUE (user_id) guarantees at
  // most one row.
  const { data, error } = await supabase
    .from(form.table)
    .select("id")
    .eq("user_id", userId)
    .maybeSingle();

  if (error) {
    console.error("Feedback status check failed:", error);
    return null;
  }

  return !!data;
};
